'use client'

import React from 'react'
import { TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TranscriptionFailureCode } from '@kiao-voice/core'
import type { ItemStageView } from './stage-progress'

/**
 * The list row's progress presentation.
 *
 * Three exports rather than one component because the three pieces live in
 * three different cells of the existing `.trow` grid — the stage text replaces
 * the meta line, the percentage replaces the status pill, and the rail is a
 * sibling of the row link. Wrapping them in one component would mean rebuilding
 * the row's layout, which is exactly what this redesign avoids: a processing row
 * and a finished row must stay the same height so the list never jumps.
 */

/**
 * Meta cell while an item is being worked on: stage in ink, sub-stage muted.
 *
 * `trailing` exists because this cell REPLACES the row's meta line, which also
 * carries the team-scope uploader attribution. That attribution belongs to every
 * row regardless of state, so it is passed through rather than lost — dropping
 * it on failed rows was a real regression the first time round.
 */
export function RowStageText({
  view,
  trailing = null,
  errorCode,
}: {
  view: ItemStageView
  trailing?: React.ReactNode
  errorCode?: TranscriptionFailureCode | null
}) {
  const { t } = useTranslation('web')

  if (view.failed) {
    return (
      <div className="tr-stage is-fail">
        <TriangleAlert className="ico" aria-hidden="true" />
        <span>
          {t(
            errorCode === 'insufficient_credits'
              ? 'transcriptions.progress.failedRowCredits'
              : errorCode === 'file_too_long_for_plan'
                ? 'transcriptions.progress.failedRowTooLong'
                : 'transcriptions.progress.failedRow'
          )}
        </span>
        {trailing}
      </div>
    )
  }

  // Queued with nothing to report: one sentence carries the whole message,
  // because a stage/sub-stage pair would imply work is under way.
  if (view.indeterminate) {
    return (
      <div className="tr-stage" role="status" aria-live="polite">
        <span>{t('transcriptions.progress.submitted')}</span>
        {trailing}
      </div>
    )
  }

  return (
    // Announced by screen readers, and ONLY this element is live: the stage pair
    // changes a handful of times per file, while the percentage next to it
    // changes every two seconds. A live region containing the number would make
    // the page unusable with a screen reader on.
    <div className="tr-stage" role="status" aria-live="polite">
      <span>{t(view.stageLabelKey)}</span>
      {view.subStageLabelKey ? (
        <>
          <span className="sep" aria-hidden="true">
            ·
          </span>
          <span className="sub">{t(view.subStageLabelKey)}</span>
        </>
      ) : null}
      {trailing}
    </div>
  )
}

/** Status cell while an item is being worked on: the global percentage, or nothing. */
export function RowStagePercent({ view }: { view: ItemStageView }) {
  // A failed row already says so in the meta cell, with an icon. Repeating it as
  // a status pill here would spend the row's one status slot saying the same
  // thing twice — the cell stays empty and the row keeps its height.
  if (view.failed) return <span className="tr-pct" />
  // Blank, never "0%". Zero reads as "hasn't started" — a claim the app cannot
  // make about a file the server is already holding.
  if (view.percent === null) return <span className="tr-pct" />
  return <span className="tr-pct">{`${view.percent}%`}</span>
}

/**
 * The row's bottom hairline, filling with ink.
 *
 * The percentage is written to `--p` and nothing else changes between ticks, so
 * the CSS transition keeps running instead of restarting — see the note on
 * `.pline` in kiao-transcripts.css.
 */
export function RowProgressRail({ view }: { view: ItemStageView }) {
  const { t } = useTranslation('web')
  // A failure the server never located leaves nothing to freeze. A red rail at
  // 0% would claim it died before it started, which is a stronger statement than
  // the data supports — so the icon and the copy carry the failure alone.
  if (view.failed && view.percent === null) return null

  const classNames = ['pline', 'trow-rail']
  if (view.failed) classNames.push('is-fail')
  else if (view.indeterminate) classNames.push('is-indet')
  else if (view.complete) classNames.push('is-done')

  return (
    <div
      className={classNames.join(' ')}
      style={{ '--p': `${view.percent ?? 0}%` } as React.CSSProperties}
      role="progressbar"
      aria-label={t('transcriptions.progress.railAria')}
      // Omitted entirely when there is no percentage. Filling in 0 would tell
      // assistive tech the work has not started, which is a different claim.
      {...(view.percent === null
        ? {}
        : { 'aria-valuenow': view.percent, 'aria-valuemin': 0, 'aria-valuemax': 100 })}
    >
      <i />
    </div>
  )
}
